import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, Trash2, Megaphone, Image as ImageIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export default function SiteAdsManagement() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const siteAdFileRef = useRef<HTMLInputElement>(null);
  const [uploadingSiteAd, setUploadingSiteAd] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: siteAdSettings } = useQuery({
    queryKey: ["site_ad"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "site_ad")
        .single();
      return (data?.setting_value as any) ?? { enabled: false, image_url: "" };
    },
  });

  const siteAdEnabled = siteAdSettings?.enabled ?? false;
  const siteAdImageUrl = siteAdSettings?.image_url || "";

  const updateSiteAd = useMutation({
    mutationFn: async (newValue: { enabled: boolean; image_url: string }) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ setting_value: newValue as any, updated_at: new Date().toISOString() })
        .eq("setting_key", "site_ad");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site_ad"] });
    },
  });

  const handleSiteAdToggle = (checked: boolean) => {
    updateSiteAd.mutate({ enabled: checked, image_url: siteAdImageUrl });
    toast.success(isRTL ? (checked ? "تم تفعيل إعلان الموقع" : "تم تعطيل إعلان الموقع") : (checked ? "Site ad enabled" : "Site ad disabled"));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error(isRTL ? "نوع الصورة غير مدعوم. JPG, PNG, WEBP, GIF" : "Unsupported type. JPG, PNG, WEBP, GIF");
      if (siteAdFileRef.current) siteAdFileRef.current.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(isRTL ? `حجم الصورة يجب أن يكون أقل من 5 ميجابايت (${(file.size / 1024 / 1024).toFixed(2)}MB)` : `Image must be less than 5MB (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      if (siteAdFileRef.current) siteAdFileRef.current.value = "";
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const cancelPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPendingFile(null);
    setPreviewUrl(null);
    if (siteAdFileRef.current) siteAdFileRef.current.value = "";
  };

  const confirmUpload = async () => {
    const file = pendingFile;
    if (!file) return;
    setUploadingSiteAd(true);
    try {
      const fileName = `site-ad-${Date.now()}.${file.name.split('.').pop()}`;
      const { error: uploadError } = await supabase.storage.from("ad-images").upload(`site-ads/${fileName}`, file);
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("ad-images").getPublicUrl(`site-ads/${fileName}`);
      updateSiteAd.mutate({ enabled: siteAdEnabled, image_url: urlData.publicUrl });
      toast.success(isRTL ? "تم تحديث صورة إعلان الموقع" : "Site ad image updated");
      cancelPreview();
    } catch {
      toast.error(isRTL ? "فشل رفع الصورة" : "Failed to upload image");
    } finally {
      setUploadingSiteAd(false);
    }
  };

  const handleRemoveSiteAdImage = () => {
    updateSiteAd.mutate({ enabled: siteAdEnabled, image_url: "" });
    toast.success(isRTL ? "تم حذف صورة إعلان الموقع" : "Site ad image removed");
  };

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/admin-dashboard")} 
        className="flex items-center gap-2 mb-4"
      >
        {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
        {isRTL ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
      </Button>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ImageIcon className="w-8 h-8 text-primary" />
          {isRTL ? 'إعلانات الموقع الثابتة' : 'Fixed Site Ads'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isRTL ? 'إدارة الإعلانات الثابتة التي تظهر في جميع صفحات الموقع' : 'Manage fixed ads that appear on all site pages'}
        </p>
      </div>

      {/* Site Ad Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-primary" />
            {isRTL ? 'إعلان جانبي ثابت' : 'Fixed Sidebar Ad'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 text-xs sm:text-sm">
            <span className="font-bold text-primary">
              {isRTL ? "📐 المقاس الموصى به:" : "📐 Recommended size:"}
            </span>
            <span className="font-mono">300 × 600 px</span>
            <span className="text-muted-foreground">
              {isRTL ? "(عمودي — JPG/PNG/WEBP — حتى 5MB)" : "(vertical — JPG/PNG/WEBP — up to 5MB)"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{isRTL ? 'حالة الإعلان' : 'Ad Status'}</p>
              <p className="text-sm text-muted-foreground">
                {isRTL ? 'يظهر في جميع صفحات الموقع بدون اقتصاص' : 'Shows on all pages without cropping'}
              </p>
            </div>
            <Switch
              checked={siteAdEnabled}
              onCheckedChange={handleSiteAdToggle}
              disabled={updateSiteAd.isPending}
            />
          </div>

          <input
            ref={siteAdFileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleFileSelect}
          />

          {previewUrl ? (
            <div className="space-y-3 max-w-sm">
              <div className="relative rounded-lg overflow-hidden border-2 border-primary">
                <img src={previewUrl} alt="preview" className="w-full h-auto" />
                <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded">
                  {isRTL ? "معاينة" : "Preview"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {pendingFile?.name} • {((pendingFile?.size || 0) / 1024).toFixed(0)} KB
              </p>
              <div className="flex gap-2">
                <Button onClick={confirmUpload} disabled={uploadingSiteAd} className="flex-1">
                  <Check className="h-4 w-4 mr-2" />
                  {uploadingSiteAd ? (isRTL ? "جاري الحفظ..." : "Saving...") : (isRTL ? "حفظ" : "Save")}
                </Button>
                <Button variant="outline" onClick={cancelPreview} disabled={uploadingSiteAd} className="flex-1">
                  <X className="h-4 w-4 mr-2" />
                  {isRTL ? "إلغاء" : "Cancel"}
                </Button>
              </div>
            </div>
          ) : siteAdImageUrl ? (
            <div className="relative max-w-sm rounded-lg overflow-hidden border">
              <img src={siteAdImageUrl} alt="Site Ad" className="w-full h-auto" />
              <div className="absolute top-2 right-2 flex gap-2">
                <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => siteAdFileRef.current?.click()} disabled={uploadingSiteAd}>
                  <Upload className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="destructive" className="h-8 w-8" onClick={handleRemoveSiteAdImage}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors max-w-sm"
              onClick={() => siteAdFileRef.current?.click()}
            >
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {uploadingSiteAd
                  ? (isRTL ? 'جاري الرفع...' : 'Uploading...')
                  : (isRTL ? 'اضغط لرفع صورة الإعلان' : 'Click to upload ad image')
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isRTL ? "JPG / PNG / WEBP — حتى 5MB" : "JPG / PNG / WEBP — up to 5MB"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
